import { capitalize } from 'lodash'

import { CostCenterForSelectFragment } from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { CostCenterSelector } from 'modules/finance/components/CostCenterSelector'
import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { FilterValue } from 'modules/finance/components/FinancialWorkspace/types/filter'
import { LocationSelector } from 'modules/finance/components/LocationSelector'

import EmployeeSelector from './EmployeeSelector'
import { JobRoleSelector } from './JobRoleSelector'
import { LegalEntityMultiSelector } from './LegalEntityMultiSelector'

export const useDefaultFilters = (recentlyAddedFilter: string | undefined) => {
  const { savedFilters, saveFilters } = useFilterProvider()
  const { dispatch, state } = useFinancialWorkspace()

  // Helper to normalize employee data - handles both old (objects) and new (IDs) formats
  const normalizeEmployeeData = (
    data: EmployeeForSelectFragmentNoAccess[] | string[] | undefined
  ): Array<{ id: string }> | undefined => {
    if (!data || data.length === 0) return undefined

    // Type guard to check if it's an array of strings
    const isStringArray = (
      arr: EmployeeForSelectFragmentNoAccess[] | string[]
    ): arr is string[] => {
      return typeof arr[0] === 'string'
    }

    // Check if it's an array of strings (new format) or objects (old format)
    if (isStringArray(data)) {
      return data.map((id) => ({ id }))
    }

    // It's already an array of objects (old format)
    return data
  }

  return useUnsafeMemo((): Record<string, FilterValue> => {
    return {
      legalEntityFilter: {
        component: (
          <LegalEntityMultiSelector
            selectedLegalEntities={state.selectedLegalEntityIds}
            defaultLegalEntities={savedFilters.selectedLegalEntityIds}
            onSelectLegalEntities={(legalEntityIds) => {
              dispatch({ type: 'SET_LEGAL_ENTITIES', payload: legalEntityIds })
              saveFilters('selectedLegalEntityIds', legalEntityIds)
            }}
          />
        ),
        label: i18n.t('finance.invoices.detail.fields.legal_entity'),
        value: 'legalEntityFilter',
        key: 'selectedLegalEntityIds',
        grouped: false,
        onReset: () => dispatch({ type: 'SET_LEGAL_ENTITIES' }),
      },
      costCenterFilter: {
        component: (
          <CostCenterSelector
            filter
            defaultOpen={recentlyAddedFilter === 'costCenterFilter'}
            value={
              state.costCenterIds?.map((id) => ({
                id,
                name: '',
                code: null,
              })) ?? []
            }
            onChange={(costCenters: CostCenterForSelectFragment[]) => {
              const ids = costCenters.map((cc) => cc.id)
              dispatch({ type: 'SET_COST_CENTERS', payload: ids })
              saveFilters('costCenterIds', ids)
            }}
          />
        ),
        label: i18n.t('bookkeepers.incidence.filters.types.cost_center'),
        value: 'costCenterFilter',
        key: 'costCenterIds',
        onReset: () => dispatch({ type: 'SET_COST_CENTERS' }),
      },
      employeeFilter: {
        component: (
          <EmployeeSelector
            defaultEmployees={normalizeEmployeeData(savedFilters.selectedEmployees)}
            defaultOpen={recentlyAddedFilter === 'employeeFilter'}
            onChange={(value) => {
              dispatch({ type: 'SET_EMPLOYEES', payload: value })
              saveFilters('selectedEmployees', value)
            }}
          />
        ),
        label: capitalize(i18n.t('common.person.other')),
        value: 'employeeFilter',
        key: 'selectedEmployeeIds',
        onReset: () => dispatch({ type: 'SET_EMPLOYEES' }),
      },
      jobRoleFilter: {
        component: (
          <JobRoleSelector
            defaultValue={savedFilters.referenceContractTreeLevelUuids}
            selectedValue={state.referenceContractTreeLevelUuids}
            defaultOpen={recentlyAddedFilter === 'jobRoleFilter'}
            onChange={(value) => {
              dispatch({ type: 'SET_REFERENCE_CONTRACT_TREE_LEVEL_UUIDS', payload: value })
              saveFilters('referenceContractTreeLevelUuids', value)
            }}
          />
        ),
        label: i18n.t('employees.employees_list.filters.job_role'),
        value: 'jobRoleFilter',
        key: 'referenceContractTreeLevelUuids',
        onReset: () => dispatch({ type: 'SET_REFERENCE_CONTRACT_TREE_LEVEL_UUIDS' }),
      },
      locationFilter: {
        component: (
          <LocationSelector
            selectedLocationIds={state.locationIds}
            defaultLocationIds={savedFilters.locationIds}
            onChangeLocationIds={(value) => {
              dispatch({ type: 'SET_LOCATIONS', payload: value })
              saveFilters('locationIds', value)
            }}
            defaultOpen={recentlyAddedFilter === 'locationFilter'}
            composeActiveLabel={(selected) => `(${selected.length})`}
            defaultLabel={i18n.t('finance.financial_workspace.filters.workplace')}
          />
        ),
        label: i18n.t('settings.locations.title'),
        value: 'locationFilter',
        key: 'locationIds',
        onReset: () => dispatch({ type: 'SET_LOCATIONS' }),
      },
    }
  }, [state, dispatch, saveFilters, savedFilters, recentlyAddedFilter])
}
